import { useMemo } from 'react';
import { useListAttachmentsQuery } from 'core/attachments/api';
import { Language } from 'core/translation/i18next/i18next';
import { commissionLanguages } from 'modules/commissions';
import { useGetOneCustomerQuery } from 'modules/customers';
import { TMailModalFormSchema, TMailModalTransformedValues } from 'modules/ui/components/MailModal/MailModal.types';

import { useGetOneInvoiceQuery, useMailInvoiceMutation } from '../api';

export const useMailInvoice = (invoiceId: number) => {
    const { data: attachments, isFetching: isFetchingAttachments } = useListAttachmentsQuery({
        invoiceId,
        withCommissionsDeliveryNotes: true,
    });
    const { data: invoice, isFetching: isFetchingInvoice } = useGetOneInvoiceQuery({ id: String(invoiceId) });
    const { data: customer, isFetching: isFetchingCustomer } = useGetOneCustomerQuery(
        { id: invoice?.customer_id?.toString() || '' },
        { skip: !invoice?.customer_id },
    );
    const [mailInvoice, { isLoading: isSending }] = useMailInvoiceMutation();

    const mailAttachments = useMemo((): TMailModalFormSchema['attachments'] => {
        if (!attachments || attachments.length === 0) return [];
        return attachments.map(({ attachment_id, name }) => ({ value: attachment_id, label: name }));
    }, [attachments]);

    const mailRecipients = useMemo((): TMailModalFormSchema['recipients'] => {
        if (!customer) return [];
        return [
            {
                value: customer.email,
                label: customer.email,
            },
        ];
    }, [customer]);
    const languageCode: Language | null = useMemo(() => {
        if (!invoice) return null;
        return commissionLanguages[invoice.language] as unknown as Language;
    }, [invoice]);

    const handleMailInvoice = async ({ recipients, message }: TMailModalTransformedValues) => {
        try {
            if (!invoice || !languageCode) throw new Error('Missing data for invoice mailing');
            const { message: resMessage, rejectedEmails } = await mailInvoice({
                emails: recipients,
                message,
                invoiceId,
                lang: languageCode,
            }).unwrap();

            return {
                success: true,
                message: resMessage,
                rejectedEmails,
            };
        } catch (error: any) {
            return {
                success: false,
                message: error?.data?.message,
                error: error,
            };
        }
    };

    return {
        isSending,
        languageCode,
        handleMailInvoice,
        recipients: mailRecipients,
        attachments: mailAttachments,
        isFetching: isFetchingAttachments || isFetchingInvoice || isFetchingCustomer,
    };
};
